// src/pages/blog/Blog.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faUser, faArrowRight, faBookOpen } from '@fortawesome/free-solid-svg-icons';

const posts = [
    {
        slug: "automatizacion-programas-fidelizacion",
        title: "Automatización de Programas de Fidelización: Casos de Éxito y Estrategias Innovadoras",
        description: "Descubre cómo la automatización transforma la relación con tus clientes, optimizando procesos y generando experiencias inolvidables.",
        date: "2024-01-15",
        author: "Equipo PetitPrime",
        readTime: "5 min"
    },
    {
        slug: "plataforma-omnicanal-de-fidelizacion",
        title: "Plataforma Omnicanal de Fidelización: Conecta con tus Clientes en Cada Paso",
        description: "Integra diversos canales para ofrecer una experiencia personalizada y coherente que fortalezca la lealtad de tus clientes.",
        date: "2024-01-10",
        author: "Equipo PetitPrime",
        readTime: "4 min"
    },
    {
        slug: "programa-fidelizacion-digital-para-comercios",
        title: "Programa de Fidelización Digital para Comercios: Estrategias que Conquistan Clientes",
        description: "Implementa un programa digital que revolucione la experiencia de tus clientes y aumente las ventas en tu comercio.",
        date: "2024-01-05",
        author: "Equipo PetitPrime",
        readTime: "6 min"
    },
    {
        slug: "sistema-fidelizacion-integracion-pos-crm",
        title: "Sistema de Fidelización con Integración POS y CRM: La Sinergia Perfecta para tu Negocio",
        description: "Revoluciona la gestión de clientes integrando POS y CRM, y potencia las estrategias de fidelización con datos precisos.",
        date: "2024-01-01",
        author: "Equipo PetitPrime",
        readTime: "7 min"
    },
    {
        slug: "software-fidelizacion-pymes",
        title: "Software de Fidelización para Pymes: Potencia tu Negocio",
        description: "Descubre cómo un software de fidelización puede transformar la relación con tus clientes y acelerar el crecimiento de tu pyme.",
        date: "2023-12-28",
        author: "Equipo PetitPrime",
        readTime: "5 min"
    }
];

const Blog = () => {
    return (
        <div className="min-h-screen bg-gray-50">
            <Helmet>
                <title>Blog de Fidelización Digital | PetitPrime - Estrategias y Consejos</title>
                <meta name="description" content="Descubre las últimas tendencias y estrategias en fidelización de clientes, software de lealtad y programas de fidelización digital para pymes y comercios." />
                <meta name="keywords" content="blog fidelización, estrategias lealtad, software pymes, programa fidelización digital" />
            </Helmet>

            {/* Hero Section */}
            <header className="bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 text-white py-20">
                <div className="container mx-auto px-4 text-center">
                    <h1 className="text-5xl lg:text-6xl font-bold mb-6">
                        Blog de Fidelización
                        <span className="text-yellow-400 block">Digital</span>
                    </h1>
                    <p className="text-xl mb-8 text-blue-100 max-w-3xl mx-auto">
                        Descubre las mejores estrategias, tendencias y consejos para transformar
                        la relación con tus clientes y potenciar tu negocio.
                    </p>
                    <div className="flex items-center justify-center text-blue-200">
                        <FontAwesomeIcon icon={faBookOpen} className="mr-2" />
                        <span>Conocimiento experto en fidelización de clientes</span>
                    </div>
                </div>
            </header>

            {/* Blog Posts */}
            <section className="py-20">
                <div className="container mx-auto px-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {posts.map((post) => (
                            <article key={post.slug} className="bg-white rounded-2xl shadow-lg overflow-hidden transform hover:scale-105 transition-all duration-300 border border-gray-100">
                                <div className="p-8">
                                    <div className="flex items-center text-sm text-gray-500 mb-4">
                                        <FontAwesomeIcon icon={faUser} className="mr-2" />
                                        <span>{post.author}</span>
                                        <span className="mx-2">•</span>
                                        <span>{post.readTime}</span>
                                    </div>

                                    <h2 className="text-xl font-bold mb-4 text-gray-800 leading-tight">
                                        <Link to={`/blog/${post.slug}`} className="hover:text-blue-600 transition-colors duration-300">
                                            {post.title}
                                        </Link>
                                    </h2>

                                    <p className="text-gray-600 mb-6 leading-relaxed">
                                        {post.description}
                                    </p>

                                    <Link
                                        to={`/blog/${post.slug}`}
                                        className="inline-flex items-center text-blue-600 font-semibold hover:text-blue-800 transition-colors duration-300"
                                    >
                                        Leer artículo completo
                                        <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                                    </Link>
                                </div>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
                <div className="container mx-auto px-4 text-center">
                    <h2 className="text-4xl font-bold mb-6">
                        ¿Listo para Aplicar lo que Aprendiste?
                    </h2>
                    <p className="text-xl mb-8 text-blue-100 max-w-2xl mx-auto">
                        Pon en práctica estas estrategias con PetitPrime y transforma
                        la fidelización de tus clientes
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Link
                            to="/register"
                            className="bg-yellow-400 hover:bg-yellow-500 text-blue-900 font-bold py-4 px-8 rounded-full text-lg shadow-lg transform hover:scale-105 transition-all duration-300 flex items-center justify-center"
                        >
                            Comenzar Prueba Gratis
                            <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                        </Link>
                        <Link
                            to="/features"
                            className="border-2 border-white hover:bg-white hover:text-blue-600 text-white font-bold py-4 px-8 rounded-full text-lg transition-all duration-300 flex items-center justify-center"
                        >
                            Ver Funcionalidades
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Blog;
